import React from "react";
import { getTimezonesWithOffset } from "../../../utils/constants/timezones";
import { getEmailLanguages } from "../../../utils/constants/languages";
import { getCountries } from "../../../utils/constants/countries";
import {
  FIELD_BASE,
  FIELD_LABEL,
  fieldState,
} from "../../../components/Form/formFieldStyles";
import FormSelect from "../../../components/Form/FormSelect";

// Read-only rows sit at the same type scale as the inputs they replace, so the
// card does not change size between view and edit mode.
const READ_ONLY_CLASS =
  "block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-800";

const GENDERS = ["Male", "Female", "Other"].map((g) => ({ value: g, label: g }));

// Option sources for every select rendered by this field.
const SELECT_OPTIONS = {
  gender: () => GENDERS,
  language_email: getEmailLanguages,
  time_zone: getTimezonesWithOffset,
  country: getCountries,
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

const FormField = React.memo(
  ({
    fieldKey,
    label,
    type = "text",
    required = false,
    readOnly = false,
    isEditing,
    formData,
    admin,
    handleChange,
  }) => {
    let value = isEditing ? formData[fieldKey] ?? "" : admin[fieldKey] ?? "-";

    if (fieldKey === "createdAt" || fieldKey === "updatedAt") {
      value = value ? formatDateTime(value) : "-";
    }

    // Capitalize gender for display in view mode
    if (!isEditing && fieldKey === "gender" && value && value !== "-") {
      value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }

    // Links stay clickable when the field is only being displayed.
    const displayValue =
      type === "url" && value && value !== "-" ? (
        <a
          href={/^https?:\/\//i.test(value) ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 hover:underline break-all"
        >
          {value}
        </a>
      ) : (
        value || "-"
      );

    if (readOnly) {
      return (
        <div>
          <label className={FIELD_LABEL}>
            {label} {required && <span className="text-danger-500">*</span>}
          </label>
          <p className={READ_ONLY_CLASS}>{displayValue}</p>
        </div>
      );
    }

    return (
      <div>
        <label htmlFor={fieldKey} className={FIELD_LABEL}>
          {label} {required && <span className="text-danger-500">*</span>}
        </label>

        {isEditing ? (
          type === "select" ? (
            <FormSelect
              name={fieldKey}
              value={value}
              onChange={handleChange}
              options={SELECT_OPTIONS[fieldKey]?.() || []}
              placeholder={`Select ${label}`}
            />
          ) : (
            <input
              id={fieldKey}
              type={type}
              name={fieldKey}
              value={value}
              onChange={handleChange}
              className={`${FIELD_BASE} ${fieldState(false)} text-gray-900`}
              placeholder={
                type === "url" ? "https://www.linkedin.com/in/username" : `Your ${label}`
              }
            />
          )
        ) : (
          <p className={READ_ONLY_CLASS}>{displayValue}</p>
        )}
      </div>
    );
  }
);

export default FormField;